import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ChevronDownIcon, BookOpenIcon, ActivityIcon, TargetIcon, DumbbellIcon, ArmchairIcon, CalendarDaysIcon } from 'lucide-react';
const modules = [{
  num: '1',
  icon: BookOpenIcon,
  title: 'Entendé tu columna antes de moverla',
  tagline: 'Porque hacer ejercicios sin entender tu diagnóstico puede hacer más mal que bien',
  bullets: ['Qué es cada curvatura y por qué aparece', 'Cómo leer tu diagnóstico y qué significa cada término', 'Qué ejercicios están prohibidos según tu tipo de curvatura']
}, {
  num: '2',
  icon: ActivityIcon,
  title: 'Protocolo de movilidad y activación',
  tagline: 'El punto de partida que el 90% de la gente saltea y por eso no ve resultados',
  bullets: ['Rutina de movilización articular progresiva', 'Activación muscular profunda antes de cualquier ejercicio', 'Respiración postural: la herramienta más subestimada de la kinesiología']
}, {
  num: '3',
  icon: TargetIcon,
  title: 'Rutinas específicas por curvatura',
  tagline: 'El corazón del Megapack: tu protocolo personalizado según tu diagnóstico',
  bullets: ['Rutina completa para escoliosis (ejercicios de Schroth adaptados)', 'Rutina completa para hipercifosis y para hiperlordosis lumbar', 'Rutina para rectificación cervical y dorsal']
}, {
  num: '4',
  icon: DumbbellIcon,
  title: 'Fortalecimiento postural progresivo',
  tagline: 'Para que los cambios que lograste se mantengan en el tiempo',
  bullets: ['Ejercicios de core profundo para estabilizar la columna', 'Fortalecimiento de cadena posterior', 'Progresión de 4 semanas con nivel principiante, intermedio y avanzado']
}, {
  num: '5',
  icon: ArmchairIcon,
  title: 'Postura en la vida diaria',
  tagline: 'Porque de nada sirve hacer 20 minutos de ejercicio si las otras 23 horas los destruís',
  bullets: ['Cómo sentarse, pararse y dormir según tu curvatura', 'Ergonomía en el trabajo y el estudio', 'Hábitos posturales para adolescentes en etapa de crecimiento']
}, {
  num: '6',
  icon: CalendarDaysIcon,
  title: 'Plan de 90 días',
  tagline: 'El camino completo, semana a semana, para que nunca te preguntes qué hacer hoy',
  bullets: ['Calendario detallado de rutinas', 'Control de progreso y señales de mejora', 'Cuándo volver al profesional y qué decirle']
}];
export function Modules() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-14 md:py-24 bg-slate-50" data-id="element-193">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-194">
        <div className="text-center mb-10 md:mb-14" data-id="element-195">
          <span className="inline-block text-brand-accent font-bold text-xs uppercase tracking-wider mb-3" data-id="element-196">
            6 Módulos Especializados
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-197">
            Qué incluye el Megapack
          </h2>
          <div className="w-20 h-1 bg-brand-accent mx-auto rounded-full" data-id="element-198"></div>
          <p className="text-slate-500 mt-4 text-sm md:text-base" data-id="element-199">
            Tocá cada módulo para ver el contenido completo
          </p>
        </div>

        <div className="space-y-3" data-id="element-200">
          {modules.map((mod, index) => {
          const isOpen = openIndex === index;
          const Icon = mod.icon;
          return <motion.div key={index} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.3,
            delay: index * 0.05
          }} className={`bg-white rounded-2xl border overflow-hidden transition-all duration-300 ${isOpen ? 'border-brand-accent shadow-lg shadow-brand-accent/10' : 'border-slate-200 shadow-sm hover:border-slate-300'}`} data-id="element-201">
                <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full text-left p-4 md:p-5 flex items-center gap-3 md:gap-5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent focus-visible:ring-offset-2 rounded-2xl" aria-expanded={isOpen} data-id="element-202">
                  {/* Number badge with icon */}
                  <div className={`flex-shrink-0 relative w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-brand-accent' : 'bg-slate-900'}`} data-id="element-203">
                    <Icon className={`w-5 h-5 md:w-6 md:h-6 transition-colors ${isOpen ? 'text-white' : 'text-brand-accent'}`} data-id="element-204" />
                    <span className={`absolute -top-1.5 -right-1.5 w-5 h-5 md:w-6 md:h-6 rounded-full flex items-center justify-center text-[10px] md:text-xs font-black border-2 border-white transition-colors ${isOpen ? 'bg-white text-brand-accent' : 'bg-brand-accent text-white'}`} data-id="element-205">
                      {mod.num}
                    </span>
                  </div>

                  {/* Title */}
                  <div className="flex-grow min-w-0" data-id="element-206">
                    <div className="text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-207">
                      Módulo {mod.num}
                    </div>
                    <h3 className="text-sm md:text-lg font-bold text-slate-900 leading-snug" data-id="element-208">
                      {mod.title}
                    </h3>
                  </div>

                  {/* Chevron */}
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-brand-accent text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-209">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-210" />
                  </motion.div>
                </button>

                <AnimatePresence initial={false} data-id="element-211">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-212">
                      <div className="px-4 md:px-5 pb-5 md:pb-6 pl-[76px] md:pl-[100px]" data-id="element-213">
                        <p className="text-xs md:text-sm italic text-slate-500 mb-4 border-l-2 border-brand-accent pl-3" data-id="element-214">
                          "{mod.tagline}"
                        </p>
                        <ul className="space-y-2.5" data-id="element-215">
                          {mod.bullets.map((bullet, i) => <motion.li key={i} initial={{
                      opacity: 0,
                      x: -10
                    }} animate={{
                      opacity: 1,
                      x: 0
                    }} transition={{
                      delay: i * 0.08
                    }} className="flex items-start gap-2.5" data-id="element-216">
                              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-brand-accent/10 flex items-center justify-center mt-0.5" data-id="element-217">
                                <CheckIcon className="w-3 h-3 text-brand-accent stroke-[3]" data-id="element-218" />
                              </div>
                              <span className="text-slate-700 text-sm leading-relaxed" data-id="element-219">
                                {bullet}
                              </span>
                            </motion.li>)}
                        </ul>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </motion.div>;
        })}
        </div>

        {/* Mobile-friendly hint */}
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} className="mt-8 text-center" data-id="element-220">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-xs md:text-sm text-slate-600 shadow-sm" data-id="element-221">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" data-id="element-222"></span>
            <span className="font-medium" data-id="element-223">
              Más de 150 ejercicios paso a paso incluidos
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}